import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Target, TrendingUp, FileCheck2, AlertTriangle, RefreshCcw, Gauge
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend
} from 'recharts';

const KpiCard = ({ icon: Icon, label, value, suffix = '', color = '#475569', tone = 'slate' }) => {
  const toneMap = {
    slate: 'bg-slate-50 border-slate-200',
    emerald: 'bg-emerald-50 border-emerald-100',
    blue: 'bg-blue-50 border-blue-100',
    amber: 'bg-amber-50 border-amber-100',
    red: 'bg-red-50 border-red-100'
  };
  return (
    <Card className={`border ${toneMap[tone]}`}>
      <CardContent className="p-4">
        <Icon className="w-5 h-5" style={{ color }} />
        <p className="text-3xl font-bold mt-2" style={{ color, fontFamily: 'var(--font-heading)' }}>
          {value}{suffix}
        </p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </CardContent>
    </Card>
  );
};

const AccuracyDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getAccuracyDashboard(getAuthHeaders());
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to load accuracy dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'ReasonDMS | Extraction Accuracy';
    load();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500" data-testid="accuracy-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
        Loading accuracy metrics…
      </div>
    );
  }

  if (!data) return null;

  const byDocType = Object.entries(data.by_doc_type || {}).map(([doc_type, accuracy]) => ({ doc_type, accuracy }));
  const confidencePct = Math.round((data.confidence_avg || 0) * 1000) / 10;

  return (
    <div data-testid="accuracy-dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Extraction Accuracy</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Field-level accuracy = (extracted fields − genuine corrections) ÷ extracted fields. Only 1–2★ feedback counts as a correction.
          </p>
        </div>
        <Button data-testid="accuracy-refresh-btn" variant="outline" size="sm" onClick={load}>
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Target}
          label="Overall Accuracy"
          value={data.overall_accuracy ?? 0}
          suffix="%"
          color="#059669"
          tone="emerald"
          data-testid="kpi-overall-accuracy"
        />
        <KpiCard icon={FileCheck2} label="Fields Extracted" value={data.total_fields_extracted ?? data.total_extractions ?? 0} color="#2563eb" tone="blue" />
        <KpiCard
          icon={AlertTriangle}
          label="Field Corrections"
          value={data.total_corrections ?? 0}
          color={data.total_corrections ? '#dc2626' : '#10b981'}
          tone={data.total_corrections ? 'red' : 'emerald'}
        />
        <KpiCard icon={Gauge} label="Avg Confidence" value={confidencePct} suffix="%" color="#7c3aed" tone="slate" />
      </div>

      {/* Overall accuracy banner */}
      <Card className="border-emerald-100 bg-gradient-to-r from-emerald-50 to-white" data-testid="accuracy-banner">
        <CardContent className="p-5 flex items-center gap-4">
          <div className="rounded-xl bg-emerald-100 p-3">
            <Target className="w-7 h-7 text-emerald-600" />
          </div>
          <div>
            <p className="text-4xl font-bold text-emerald-700" style={{ fontFamily: 'var(--font-heading)' }}>
              {data.overall_accuracy ?? 0}%
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              field-level extraction accuracy across {data.total_fields_extracted ?? 0} extracted fields from {data.total_extractions ?? 0} documents
              {data.total_corrections ? ` · ${data.total_corrections} field corrections` : ' · no corrections recorded'}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Accuracy by doc type */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Accuracy by document type</CardTitle></CardHeader>
          <CardContent>
            {byDocType.length === 0 ? (
              <p className="text-sm text-slate-400 py-10 text-center">No document-type data yet</p>
            ) : (
              <div style={{ width: '100%', height: 240, minWidth: 320 }} data-testid="accuracy-by-type-chart">
                <ResponsiveContainer>
                  <BarChart data={byDocType} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="doc_type" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 100]} unit="%" />
                    <Tooltip />
                    <Bar dataKey="accuracy" fill="#059669" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confidence trend */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Average confidence trend</CardTitle></CardHeader>
          <CardContent data-testid="confidence-trend-chart">
            {(data.confidence_trend || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-10 text-center">No confidence trend data yet</p>
            ) : (
              <div style={{ width: '100%', height: 240, minWidth: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={data.confidence_trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                    <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 1]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="confidence" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Extractions vs corrections trend (30d) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" />Extractions vs corrections (30 days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 240, minWidth: 320 }} data-testid="correction-trend-chart">
            <ResponsiveContainer>
              <BarChart data={data.correction_trend_30d || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="extractions" fill="#2563eb" />
                <Bar dataKey="corrections" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Low confidence fields */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Lowest-confidence fields</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="low-confidence-table">
              <thead>
                <tr className="text-xs text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 px-3">Field</th>
                  <th className="text-right py-2 px-3">Avg confidence</th>
                  <th className="text-right py-2 px-3">Samples</th>
                </tr>
              </thead>
              <tbody>
                {(data.low_confidence_fields || []).map((f) => (
                  <tr key={f.field} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium text-slate-700">{f.field.replace(/_/g, ' ')}</td>
                    <td className="text-right py-2 px-3">
                      <span className={f.avg_confidence < 0.7 ? 'text-red-600 font-medium' : 'text-slate-600'}>
                        {Math.round(f.avg_confidence * 100)}%
                      </span>
                    </td>
                    <td className="text-right py-2 px-3 text-slate-500">{f.samples}</td>
                  </tr>
                ))}
                {(data.low_confidence_fields || []).length === 0 && (
                  <tr><td colSpan={3} className="py-6 text-center text-slate-400">No field-confidence data available yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccuracyDashboard;
